import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core'

import { Option } from 'common-components'

import { AppSelectComponent } from 'common-components'
import { ToastrService } from 'ngx-toastr'
import {
  IPlan,
  IProviderCoverage,
  ProviderCoveragesODataService,
  defaultTimezone
} from '@exchange/app/domain'
import { UtilService } from '@exchange/app/services'
import $ from 'jquery'
import moment from 'moment-timezone'

@Component({
  selector: 'app-providers-coverage',
  templateUrl: './providers-coverage.component.html',
  standalone: false
})
export class ProvidersCoverageComponent implements OnChanges {
  @ViewChild('payersList') payersList: AppSelectComponent
  @ViewChild('plansList') plansList: AppSelectComponent
  @ViewChild('episodeTypesList') episodeTypesList: AppSelectComponent
  @Input() payers: Option[]
  @Input() plans: IPlan[]
  @Input() episodeTypes: Option[]
  @Input() providerCoverage: IProviderCoverage
  @Input() ianaTimeZone: string

  @Output() handleSuccess = new EventEmitter()
  @Output() handleCancel = new EventEmitter()

  planPlaceholder = 'Select a Payer first'
  payerPlans: Option[] = []
  errorMessage = ''

  busy: any = null
  payerId: number = null
  startDateVal: Date = null
  endDateVal?: Date = null

  constructor(
    private odata: ProviderCoveragesODataService,
    public util: UtilService,
    private toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    setTimeout(() => {
      if (changes.providerCoverage && changes.providerCoverage.currentValue.Id === 0) {
        document.querySelectorAll('[id^=app-datetime-]').forEach(element => {
          $(`#${element.id}`).data('DateTimePicker').date(null)
        })
        // Reset just in case of payer selected previously and hit add again
        this.planPlaceholder = 'Select a Payer first'
      }
      this.payerPlans = []
      this.setActiveEpisodeType()
      this.setActivePayer()
      this.setActivePlan()
      this.setDates()
    }, 1)
  }

  onSubmit(): void {
    let error = false

    if (this.providerCoverage.PlanIds.length === 0) {
      error = true
      this.toastr.error('At least one plan selected is required.')
    }

    if (this.providerCoverage.EpisodeTypeIds.length === 0) {
      error = true
      this.toastr.error('At least one episode type selected is required.')
    }

    if (!this.startDateVal) {
      error = true
      this.toastr.error('Start Date is required.')
    }

    if (this.endDateVal && this.startDateVal > this.endDateVal) {
      error = true
      this.toastr.error('Start Date must be before End Date')
    }

    if (!error) {
      this.toastr.clear()
      if (this.providerCoverage.Id) {
        this.odata
          .put(this.providerCoverage, this.providerCoverage.Id)
          .exec()
          .subscribe({ next: this.showSuccess.bind(this), error: this.showError.bind(this) })
      } else {
        this.odata
          .post(this.providerCoverage)
          .exec()
          .subscribe({ next: this.showSuccess.bind(this), error: this.showError.bind(this) })
      }
    }
  }

  showSuccess(): void {
    this.busy = null
    this.handleSuccess.emit()

    this.toastr.success('Coverage was saved successfully.')
  }

  showError(response): void {
    this.busy = null
    const msg = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  cancelEdit(): void {
    this.handleCancel.emit()
  }

  selectPayer = (value: any = null) => {
    if (value && value.id) {
      this.plansList.active = []
      this.payerPlans = this.plans
        .filter(x => x.PayerId === value.id)
        .map(x => new Option(x.Id, x.Name, x))
      this.planPlaceholder = this.payerPlans.length > 0 ? 'Select a Plan' : 'No available Plans'
    }
  }

  refreshPlans = (value: any = null) => {
    this.providerCoverage.PlanIds = value.map((item: any) => {
      return item.id
    })
  }

  refreshEpisodeTypes = (value: any = null) => {
    this.providerCoverage.EpisodeTypeIds = value.map((item: any) => {
      return item.id
    })
  }

  setActivePlan() {
    this.plansList.active = []
    if (this.payerPlans && this.payerPlans.length) {
      this.plansList.active = this.payerPlans.filter(x => {
        return this.providerCoverage.PlanIds.includes(x.id as number)
      })
    }
  }

  setActivePayer() {
    this.payersList.active = []
    if (this.providerCoverage && this.providerCoverage.PlanIds) {
      const foundPlan = this.plans.find(plan =>
        this.providerCoverage.PlanIds.includes(plan.Id as number)
      )
      if (foundPlan) {
        this.payersList.active = this.payers.filter(x => x.id === foundPlan.PayerId)
        this.selectPayer({ id: foundPlan.PayerId })
      }
    }
  }

  setActiveEpisodeType() {
    this.episodeTypesList.active = []
    if (this.providerCoverage && this.providerCoverage.EpisodeTypeIds) {
      this.episodeTypesList.active = this.episodeTypes.filter(x =>
        this.providerCoverage.EpisodeTypeIds.includes(x.id as number)
      )
    }
  }

  setDates() {
    if (this.providerCoverage.StartDate) {
      this.startDateVal = new Date(
        moment
          .tz(this.providerCoverage.StartDate, this.ianaTimeZone)
          .utc(true)
          .toISOString()
          .replace('Z', '')
      )
    }

    if (this.providerCoverage.EndDate) {
      this.endDateVal = new Date(
        moment
          .tz(this.providerCoverage.EndDate, this.ianaTimeZone)
          .utc(true)
          .toISOString()
          .replace('Z', '')
      )
    }
  }

  onStartDateChange(date: Date) {
    if (date) {
      const startDateStr = `${date.toISOString().split('T')[0]}T00:00:00`
      const startDate = new Date(
        moment.tz(startDateStr, this.ianaTimeZone ? this.ianaTimeZone : defaultTimezone).format()
      )
      this.providerCoverage.StartDate = startDate
    } else {
      this.providerCoverage.StartDate = null
    }
  }

  onEndDateChange(date: Date) {
    if (date) {
      const endDateStr = `${date.toISOString().split('T')[0]}T23:59:59.999`
      const endDate = new Date(
        moment.tz(endDateStr, this.ianaTimeZone ? this.ianaTimeZone : defaultTimezone).format()
      )
      this.providerCoverage.EndDate = endDate
    } else {
      this.providerCoverage.EndDate = null
    }
  }
}
